/*
 * 输入区。
 *
 * 三个刻意的决定：
 *
 * 1. **不在前端设字数上限。**
 *    CHAT-018 的规则是「输入上限 = 100 + 羁绊等级 × 100」，那是一个经济数值，
 *    权威在服务端。旧版把它算在客户端（CharacterChat.tsx:167），等级读不到时
 *    就按 0 级封成 100 字。这里改成：服务端下发了上限才拦，没下发就不拦，
 *    让服务端去拒绝。宁可被服务端退回来，也不凭猜测限制用户。
 *
 * 2. **平时不显示字数。**
 *    没到上限时那行计数只是噪音。只有真的超了，才在输入框内部底部亮一行，
 *    说清超了多少、上限是多少。
 *
 * 3. **离线也允许点发送。**
 *    禁用发送按钮会让人以为是自己哪里没填对。让它发出去、失败、给出明确的
 *    失败原因和重试入口，比一个灰掉的按钮诚实得多。
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Butterfly } from "../../components/AsyncState";
import { IconClose, IconImage, IconSend } from "../../components/Icons";
import { PowerCostNotice } from "../../components/PowerCostNotice";
import { ApiError } from "../../services/api-client";
import { uploadChatImage } from "../../services/chat-api";

export interface ComposerMessageInput {
  text: string;
  imageAssetIds?: readonly string[];
  /**
   * 刚选中的本地图片。只供本次页面乐观展示，不进 API 请求、不落消息契约。
   * ChatPage 接管并在 OSS 预览悄悄预载完成后释放 Object URL。
   */
  imagePreviews?: readonly { assetId: string; url: string }[];
}

interface PendingImage {
  id: string;
  file: File;
  assetId?: string;
}

const newPendingImageId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `image-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function Composer({
  maxLength,
  imagePowerSurcharge,
  imageMaxCount,
  imageNoticeAcknowledged,
  onAcknowledgeImageNotice,
  streaming,
  allowMedia,
  placeholder = "说点什么吧…",
  onSend,
}: {
  /** 服务端下发的输入上限。未知时为 null——不要用默认值填。 */
  maxLength: number | null;
  /** 服务端下发的每图加价。只用于首次提醒，实际扣费仍完全由服务端裁定。 */
  imagePowerSurcharge: number | null;
  /** 服务端下发的单条消息图片上限。 */
  imageMaxCount: number;
  /** null 表示确认状态还在读取；只有 true 才跳过首次提醒。 */
  imageNoticeAcknowledged: boolean | null;
  onAcknowledgeImageNotice: () => void;
  streaming: boolean;
  /** 只有服务端确认当前是 normal 形态才为真。weakened 下两个入口都不渲染。 */
  allowMedia: boolean;
  /** 不同对话入口可以说明自己的输入范围；角色聊天保持原文案。 */
  placeholder?: string;
  onSend: (input: ComposerMessageInput) => void;
}) {
  const [text, setText] = useState("");
  const [images, setImages] = useState<PendingImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [costNoticeOpen, setCostNoticeOpen] = useState(false);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const previews = useMemo(
    () => images.map((image) => ({ ...image, url: URL.createObjectURL(image.file) })),
    [images],
  );

  useEffect(
    () => () => {
      for (const preview of previews) URL.revokeObjectURL(preview.url);
    },
    [previews],
  );

  useEffect(() => {
    if (allowMedia) return;
    setImages([]);
    if (fileRef.current) fileRef.current.value = "";
  }, [allowMedia]);

  // 自适应高度。上限 8 行，再多就内部滚动，不把输入框顶到半屏。
  useEffect(() => {
    const node = areaRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 168)}px`;
  }, [text]);

  const overBy = maxLength === null ? 0 : text.length - maxLength;
  const overLimit = overBy > 0;
  const canSend =
    (text.trim().length > 0 || images.length > 0) &&
    !overLimit &&
    !streaming &&
    !uploading;
  const showNotice = (reason: string) => {
    setNotice(reason);
    window.setTimeout(() => setNotice(null), 3200);
  };

  const imageLimitNotice = `每次最多传${imageMaxCount}张图哦~`;

  const chooseImage = () => {
    if (images.length >= imageMaxCount) {
      showNotice(imageLimitNotice);
      return;
    }
    if (
      imagePowerSurcharge !== null &&
      imagePowerSurcharge > 0 &&
      imageNoticeAcknowledged !== true
    ) {
      setCostNoticeOpen(true);
      return;
    }
    fileRef.current?.click();
  };

  const addImageFiles = (files: FileList | null): void => {
    const selected = Array.from(files ?? []);
    if (fileRef.current) fileRef.current.value = "";
    if (selected.length === 0) return;

    const available = Math.max(0, imageMaxCount - images.length);
    if (available === 0 || selected.length > available) showNotice(imageLimitNotice);
    if (available === 0) return;

    setImages((current) => [
      ...current,
      ...selected.slice(0, Math.max(0, imageMaxCount - current.length)).map((file) => ({
        id: newPendingImageId(),
        file,
      })),
    ]);
  };

  const submit = async () => {
    if (!canSend) return;
    const caption = text.trim();
    if (images.length === 0) {
      onSend({ text: caption });
      setText("");
      return;
    }

    setUploading(true);
    try {
      const imageAssetIds: string[] = [];
      for (const image of images) {
        let assetId = image.assetId;
        if (!assetId) {
          const uploaded = await uploadChatImage(image.file);
          const uploadedAssetId = uploaded.assetId;
          assetId = uploadedAssetId;
          setImages((current) =>
            current.map((item) =>
              item.id === image.id ? { ...item, assetId: uploadedAssetId } : item,
            ),
          );
        }
        imageAssetIds.push(assetId);
      }
      // 输入框里的 Object URL 会随 setImages([]) 被回收；另建一份交给消息气泡，
      // 让用户已经看见的图片在服务器预览后台加载期间继续显示。
      const imagePreviews = images.map((image, index) => ({
        assetId: imageAssetIds[index]!,
        url: URL.createObjectURL(image.file),
      }));
      onSend({ text: caption, imageAssetIds, imagePreviews });
      setText("");
      setImages([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (cause) {
      showNotice(cause instanceof ApiError ? cause.message : "图片没能上传，请再试一次");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="composer">
      {notice ? (
        <p className="composer__notice fade-in" role="status">
          {notice}
        </p>
      ) : null}

      <div
        className={`composer__box${previews.length > 0 ? " composer__box--with-preview" : ""}`}
        data-image-count={previews.length || undefined}
      >
        {previews.length > 0 ? (
          <div className="composer__previews" aria-label={`已选择 ${previews.length} 张图片`}>
            {previews.map((preview, index) => (
              <figure className="composer__preview" key={preview.id}>
                <img src={preview.url} alt={`待发送图片预览 ${index + 1}`} />
                <button
                  type="button"
                  className="composer__preview-remove"
                  onClick={() => setImages((current) => current.filter((item) => item.id !== preview.id))}
                  aria-label={`移除第 ${index + 1} 张图片`}
                  title="移除图片"
                  disabled={uploading}
                >
                  <IconClose size={15} />
                </button>
              </figure>
            ))}
          </div>
        ) : null}

        {allowMedia ? (
          <div className="composer__tools">
            <input
              ref={fileRef}
              className="composer__file"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => addImageFiles(event.currentTarget.files)}
            />
            <button
              type="button"
              className="composer__tool"
              aria-label={`选择图片，已选 ${images.length} 张，最多 ${imageMaxCount} 张`}
              title={`选择图片（最多 ${imageMaxCount} 张）`}
              onClick={chooseImage}
              disabled={streaming || uploading}
            >
              <IconImage size={18} />
            </button>
          </div>
        ) : null}

        <textarea
          ref={areaRef}
          className="composer__input"
          rows={1}
          value={text}
          placeholder={
            images.length > 1
              ? "给这些图补一句话（可不填）"
              : images.length === 1
                ? "给这张图补一句话（可不填）"
                : placeholder
          }
          disabled={uploading}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            // 桌面端 Enter 发送、Shift+Enter 换行；移动端软键盘不触发这条。
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              void submit();
            }
          }}
        />

        {streaming || uploading ? (
          /*
           * 她在答的时候，发送键换成一只停在原地扇翅膀的蝴蝶。
           * **它是状态指示，不是按钮**——点不下去，也没有 onClick。
           *
           * 这里曾经先后是红色方块停止钮、然后是可点的蝴蝶。两版都在承诺
           * 一件做不到的事：服务端那一轮**照常跑完、照常扣费、照常落库**，
           * 断开的只是客户端这一侧。用户点完以为「这一轮不算了」，
           * 下次进来却看到自己那句话好端端在那儿、还带着她的完整回答。
           *
           * 而它换来的代价是实打实的：断开之后前端把**用户自己那条**标成
           * 「未送达」（那条消息在调模型之前就落库了，这是纯粹的谎），
           * 已经吐出来的半句也被抹掉。删掉这颗按钮就是删掉那条路径。
           *
           * 卡死时的出口不在这里，在 `chat-stream.ts` 的空闲看门狗。
           */
          <div
            className="composer__send composer__send--waiting"
            role="status"
            aria-label={uploading ? "正在上传图片" : "她正在回答"}
            title={uploading ? "正在上传图片" : "她在答，稍等一下。"}
          >
            <Butterfly className="composer__butterfly" />
          </div>
        ) : (
          <button
            type="button"
            className="composer__send"
            onClick={() => void submit()}
            disabled={!canSend}
            aria-label="发送"
          >
            <IconSend size={17} />
          </button>
        )}

        {/* 只有真的超了才出现，并且就在输入框里面，不另起一行占位。 */}
        {overLimit ? (
          <p className="composer__limit" role="status">
            超出 <b>{overBy}</b> 个字。这次最多能说 <b>{maxLength}</b> 字，
            羁绊等级涨上去就能说得更长。
          </p>
        ) : null}
      </div>

      <PowerCostNotice
        open={costNoticeOpen}
        onClose={() => setCostNoticeOpen(false)}
        onConfirm={() => {
          onAcknowledgeImageNotice();
          setCostNoticeOpen(false);
          // “我知道了”的点击仍是用户手势，可以直接唤起系统文件选择器。
          fileRef.current?.click();
        }}
      >
        每张图片会消耗 {imagePowerSurcharge} 点灵力哦～
      </PowerCostNotice>
    </div>
  );
}
